/**
 * Admin Transaction Service
 * Handles all transaction-related admin API calls
 */

import { getApi, postApi, deleteApi, patchApi } from '../adminApi';

/**
 * Get all transactions with filters
 * @param {Object} params - Query parameters (page, limit, status, type, userId, etc.)
 * @returns {Promise}
 */
export const getTransactions = async (params = {}) => {
  // Check if search parameters exist
  const hasSearchParams = params.searchTerm || params.dateRange || params.amountRange;
  
  if (hasSearchParams) {
    // Use search endpoint
    const searchParams = new URLSearchParams();
    searchParams.append('page', params.page || 1);
    searchParams.append('limit', params.limit || 10);
    
    if (params.searchTerm) {
      searchParams.append('searchTerm', params.searchTerm);
    }
    if (params.searchFields) {
      searchParams.append('searchFields', params.searchFields);
    }
    if (params.dateRange) {
      searchParams.append('dateRange', JSON.stringify(params.dateRange));
    }
    if (params.amountRange) {
      searchParams.append('amountRange', JSON.stringify(params.amountRange));
    }
    if (params.type) {
      searchParams.append('type', params.type);
    }
    if (params.status) {
      searchParams.append('status', params.status);
    }
    
    return getApi(`transactions/search?${searchParams.toString()}`);
  } else {
    // Use regular endpoint
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.status) queryParams.append('status', params.status);
    if (params.type) queryParams.append('type', params.type);
    
    const endpoint = queryParams.toString() ? `transactions?${queryParams.toString()}` : 'transactions';
    return getApi(endpoint);
  }
};

/**
 * Get transaction by ID
 * @param {string} transactionId - Transaction ID
 * @returns {Promise}
 */
export const getTransactionById = async (transactionId) => {
  return getApi(`transactions/${transactionId}`);
};

/**
 * Create a new transaction
 * @param {Object} payload - Transaction data
 * @returns {Promise}
 */
export const createTransaction = async (payload) => {
  return postApi('transactions/create', payload);
};

/**
 * Process a transaction
 * @param {string} transactionId - Transaction ID
 * @param {Object} data - Process data (status, remarks, etc.)
 * @returns {Promise}
 */
export const processTransaction = async (transactionId, data) => {
  return postApi(`transactions/${transactionId}/process`, data);
};

/**
 * Update transaction timestamp
 * @param {string} transactionId - Transaction ID
 * @param {string} timestamp - New timestamp
 * @returns {Promise}
 */
export const updateTransactionTimestamp = async (transactionId, timestamp) => {
  return patchApi(`transactions/${transactionId}/timestamp`, {
    createdAt: timestamp
  });
};

/**
 * Delete a transaction
 * @param {string} transactionId - Transaction ID
 * @returns {Promise}
 */
export const deleteTransaction = async (transactionId) => {
  return deleteApi(`transactions/${transactionId}`);
};

